import { WorkbenchUiAccessorType } from 'Core/Workbench'
import { ContainerType, ErrorAndWarningCount, TaskAdditionalInfo } from 'Types/Tasks.types'
import { UpdatableReactElement } from 'shared/utils/react.utils'
import { SubmitModalController } from './Modals/SubmitModalController'
import { SubmitModalMetaData } from './Modals/SubmitModalController'
import { Studio } from 'Core/Studio'
import { CommonTask } from 'Tasks/CommonTask/CommonTask'
import { ActionModalController, ActionModalMetaData } from './Modals/ActionModalController'
import { ViolationsModalController } from './Modals/ViolationsModalController'
import { ViolationsModalMetaData } from './Modals/ViolationsModalController'
import { RejectOrSkipModalMetaData } from './Modals/RejectOrSkipModalController'
import { RejectOrSkipModalController } from './Modals/RejectOrSkipModalController'
import { IContextHandler } from 'Core/ContextHandler/IContextHandler'
import { EndExamModalController } from './Modals/EndExamModalController'
import { EndExamModalMetaData } from './Modals/EndExamModalController'
import {
    SaveForLaterModalController,
    SaveForLaterModalMetaData,
} from './Modals/SaveForLaterModalController'
import { InactivityModalController } from './Modals/InactivityModalController'
import { InactivityModalMetaData } from 'Components/modal/InactivityModal/InactivityModal'

export type ModalTypeMap = {
    [SubmitModalController.Id]: SubmitModalMetaData
    [ActionModalController.Id]: ActionModalMetaData
    [ViolationsModalController.Id]: ViolationsModalMetaData
    [RejectOrSkipModalController.Id]: RejectOrSkipModalMetaData
    [EndExamModalController.Id]: EndExamModalMetaData
    [SaveForLaterModalController.Id]: SaveForLaterModalMetaData
    [InactivityModalController.Id]: InactivityModalMetaData
}

export type ModalContext = {
    task: CommonTask<ContainerType, unknown>
    studio: Studio
    isAIUsageConfirmationEnabled: boolean
    isRejected: boolean
    workbenchUiAccessor?: WorkbenchUiAccessorType
    taskAdditionalInfo: TaskAdditionalInfo
    errorAndWarningCount: ErrorAndWarningCount
    timeElapsedInSeconds: () => number
    contextHandler: IContextHandler<ContainerType, unknown>
}

export interface IModal<T = unknown> {
    isVisible: boolean
    metaData?: T
    setVisibility: (isVisible: boolean) => void
    getElement: () => UpdatableReactElement
    setMetaData: (metaData: T) => void
    updateMetaData: (partialMetaData: Partial<T>) => void
}

export interface IModalConstructor<T = unknown> {
    new (context: ModalContext): IModal<T>
    Id: string
}

export abstract class BaseModalController<T = unknown> implements IModal<T> {
    isVisible: boolean = false
    metaData?: T
    element: UpdatableReactElement
    constructor(
        public modalContext: ModalContext,
        id: string
    ) {
        this.element = this.createElement()
        modalContext.workbenchUiAccessor?.modalContainer.addModal({
            element: this.element,
            id,
        })
    }

    abstract createElement(): UpdatableReactElement

    setVisibility = (isVisible: boolean) => {
        this.isVisible = isVisible
        this.render()
    }

    setMetaData = (metaData: T) => {
        this.metaData = metaData
        this.render()
    }

    getMetaData = (): T | undefined => this.metaData

    updateMetaData = (partialMetaData: Partial<T>) => {
        if (!this.metaData || !partialMetaData) {
            return
        }
        this.metaData = {
            ...this.metaData,
            ...partialMetaData,
        }
        this.render()
    }

    getVisibility = () => this.isVisible

    getElement = () => this.element

    render = () => {
        this.element.updater.forceUpdate()
    }
}
